import { PORTFOLIO_REFETCH_POLICY } from 'config/RefetchPolicies'
import { SubgraphType } from 'config/subgraph'
import { useConnection } from 'hooks/useConnection'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import { GetPortfolioV2PositionsQuery, useGetPortfolioV2PositionsQuery } from 'queries/v2/generated'
import { ProcessedV2Position } from 'types/subgraph'
import { calculateV2TokenAmounts } from 'utils/portfolio/calculateV2TokenAmounts'
import { filterV2Positions, normalizeDisplaySymbol } from 'utils/portfolio/filterHelpers'
import { Address } from 'viem'

type V2PositionFromQuery = NonNullable<GetPortfolioV2PositionsQuery['users'][number]['liquidityPositions']>[number]

function processV2Position(position: V2PositionFromQuery): ProcessedV2Position {
  const { token0Amount, token1Amount, shareOfPool } = calculateV2TokenAmounts(
    position.liquidityTokenBalance,
    position.pair.totalSupply,
    position.pair.reserve0,
    position.pair.reserve1
  )

  const decimals0 = parseInt(position.pair.token0.decimals)
  const decimals1 = parseInt(position.pair.token1.decimals)

  return {
    id: position.id,
    pairAddress: position.pair.id as Address,
    token0: {
      id: position.pair.token0.id,
      address: position.pair.token0.id,
      symbol: normalizeDisplaySymbol(position.pair.token0.symbol),
      name: position.pair.token0.name,
      decimals: decimals0,
    },
    token1: {
      id: position.pair.token1.id,
      address: position.pair.token1.id,
      symbol: normalizeDisplaySymbol(position.pair.token1.symbol),
      name: position.pair.token1.name,
      decimals: decimals1,
    },
    liquidityTokenBalance: position.liquidityTokenBalance,
    liquidityTokenTotalSupply: position.pair.totalSupply,
    reserve0: position.pair.reserve0,
    reserve1: position.pair.reserve1,
    token0Amount,
    token1Amount,
    reserveUSD: position.pair.reserveUSD,
    shareOfPool,
    token0Price: position.pair.token0Price,
    token1Price: position.pair.token1Price,
  }
}

export function useV2Positions(options?: { enabled?: boolean; refetchInterval?: number; filter?: string }) {
  const { account: userAddress, isConnected } = useConnection()
  const subgraphConfig = useSubgraphConfig(SubgraphType.V2)

  const v2PositionsQuery = useGetPortfolioV2PositionsQuery(
    subgraphConfig,
    {
      user: userAddress?.toLowerCase() as any,
    },
    {
      enabled: isConnected && !!userAddress && options?.enabled !== false,
      ...PORTFOLIO_REFETCH_POLICY,
      refetchInterval: options?.refetchInterval ?? PORTFOLIO_REFETCH_POLICY.refetchInterval,
    }
  )

  // Process positions
  const processedPositions =
    v2PositionsQuery.data?.userV2S?.[0]?.liquidityPositions?.map((position) => processV2Position(position)) || []

  // Filter positions
  const filteredPositions = processedPositions ? filterV2Positions(processedPositions, options?.filter || '') : []

  return {
    ...v2PositionsQuery,
    data: filteredPositions,
    allPositions: processedPositions,
  }
}
